import React, { useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { XCircle } from "lucide-react";
import axios from "axios";
import { AUTH_URL } from "../../utils/url";
import { useParams } from "react-router-dom";
import PageLoader from "../common-components/page-loader/page-loader";
import { getConfig } from "../../utils/permissions";

export default function Profile() {
  const [loading, setLoading] = React.useState(true);
  const [user, setUser] = React.useState(null);
  const [formData, setFormData] = React.useState({
    firstName: "",
    lastName: "",
    organization: "",
    password: "",
  });
  const [errors, setErrors] = React.useState({});

  const { id } = useParams();

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First Name is Required!";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last Name is Required!";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onFinish = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    console.log("Submit form", formData);
    const payload = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      organization: formData.organization,
    };

    if (formData.password) {
      payload.password = formData.password;
    }

    if (id) {
      payload.target = id;
    }

    const path = id ? "/user/admin-api/user" : "/user/me";
    axios
      .patch(AUTH_URL + path, payload)
      .then(() => {
        toast.success("Changes saved.");
      })
      .catch((error) => {
        toast.error(
          "Error editing profile: " +
            (error.response?.data?.error || error.message)
        );
      });
  };

  const suspend = () => {
    const state = !user.isBanned;
    const path = "/user/admin-api/ban";
    axios
      .post(AUTH_URL + path, { target: id, state: state })
      .then(() => {
        setUser((user) => {
          const userCopy = { ...user };
          userCopy.isBanned = state;
          return userCopy;
        });
      })
      .catch(() => {
        // Do something
      });
  };
  useEffect(() => {
    setLoading(true);
    const path = id ? "/user/" + id : "/user/me";
    axios
      .get(AUTH_URL + path)
      .then((response) => {
        let user = response.data.data.user;
        setUser(user);
        setFormData({
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          organization: user.organization || "",
          password: "",
        });
      })
      .finally(() => {
        setLoading(false);
      });
    // eslint-disable-next-line
  }, []);

  if (loading || !user) {
    return <PageLoader />;
  }

  return (
    <div className="mx-auto p-6 space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onFinish} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input id="username" value={user.username} disabled />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  className={errors.firstName ? "border-red-500" : ""}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  className={errors.lastName ? "border-red-500" : ""}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" value={user.email} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                placeholder="Leave blank to keep current password"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="organization">Label Name</Label>
              <Input
                id="organization"
                value={formData.organization}
                onChange={(e) =>
                  handleInputChange("organization", e.target.value)
                }
              />
            </div>

            <Button type="submit" className="w-full md:w-auto">
              Save
            </Button>
          </form>
        </CardContent>
      </Card>

      {getConfig("privilege.can-edit-user") && id && (
        <Card>
          <CardHeader>
            <CardTitle>Administration</CardTitle>
          </CardHeader>
          <CardContent>
            <Button
              variant={user.isBanned ? "default" : "destructive"}
              onClick={suspend}
              className="flex items-center gap-2"
            >
              <XCircle className="h-4 w-4" />
              {user.isBanned ? "Unsuspend" : "Suspend"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
